import React, {
  useMemo,
  ReactNode,
  useCallback,
  memo,
  useRef,
  useLayoutEffect,
  useState,
} from 'react';
import clsx from 'clsx';

import { ProfileFieldErrorType } from '_types/profile.interface';

import CheckedIcon from 'app/components/Icons/CheckedIcon';
import ErrorCloud from 'app/components/ErrorCloud';
import SelectButtonHint from '../SelectButtonHint';

import './style.scss';

interface SelectButtonSimpleProps {
  title: string;
  value: string;
  disabled?: boolean;
  selected: boolean;
  type: 'checkbox' | 'select';
  onClick: Function;
  rightDownIcon?: ReactNode;
  leftDownIcon?: ReactNode;
  errors?: ProfileFieldErrorType;
  fieldAligment?: 'left' | 'right';
}

const SelectButtonSimple: React.FC<SelectButtonSimpleProps> = ({
  rightDownIcon,
  title,
  value,
  disabled,
  selected,
  type,
  leftDownIcon,
  onClick,
  errors,
  fieldAligment = 'left',
}) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [showOverflowingTextHint, setShowOverflowingTextHint] = useState(false);

  const fieldToClickOn = useMemo(() => {
    switch (type) {
      case 'select':
        return (
          <div
            className={clsx('select_button__circle', { active: selected })}
          />
        );
      case 'checkbox':
        return (
          <div className={clsx('select_button__box', { active: selected })}>
            {selected && <CheckedIcon className="select_button__box_icon" />}
          </div>
        );
      default:
        return <></>;
    }
  }, [selected, type]);

  const handleClick = useCallback(() => {
    if (disabled) {
      return;
    }

    onClick(!selected, value);
  }, [onClick, selected, disabled, value]);

  useLayoutEffect(() => {
    const checkOverflow = () => {
      const el = textRef.current;
      if (el) {
        setShowOverflowingTextHint(el.scrollWidth > el.clientWidth);
      }
    };

    checkOverflow();

    let observer: ResizeObserver | null = null;

    if (window.ResizeObserver) {
      observer = new ResizeObserver(checkOverflow);
      if (textRef.current) observer.observe(textRef.current);
    } else {
      window.addEventListener('resize', checkOverflow);
    }

    return () => {
      if (observer && textRef.current) observer.unobserve(textRef.current);
      else window.removeEventListener('resize', checkOverflow);
    };
  }, [title]);

  return (
    <div
      className={clsx('select_button', {
        active: selected,
        disabled,
        error: errors?.length,
      })}
      onClick={handleClick}
    >
      <div className="select_button__block">
        {fieldAligment === 'left' && fieldToClickOn}

        {leftDownIcon && (
          <div className="select_button__icon left">{leftDownIcon}</div>
        )}

        <div className="select_button__text" ref={textRef}>
          {title}
        </div>
      </div>

      {!!errors?.length && <ErrorCloud errorsListOrErrorText={errors} />}

      {showOverflowingTextHint && (
        <SelectButtonHint hint={title} position="bottom" />
      )}

      {fieldAligment === 'right' && fieldToClickOn}

      {rightDownIcon && (
        <div className="select_button__icon">{rightDownIcon}</div>
      )}
    </div>
  );
};

export default memo(SelectButtonSimple);
